import { create } from 'zustand'
import { apiService } from '../services/api.js'
import apiCallManager from '../utils/apiCallManager'

export const useCompositionStore = create((set, get) => ({
  // State
  programs: [],
  clientDetails: null,
  selectedProgram: null,
  programGoal: null,
  weights: [],
  compositions: [],
  vitals: [],
  isLoadingPrograms: false,
  isLoadingCompositions: false,
  error: null,

  // Actions
  fetchClientPrograms: async (companyUrl) => {
    const state = get()
    
    // Prevent duplicate calls if already loading or data exists
    if (state.isLoadingPrograms || (state.programs.length > 0 && state.clientDetails)) {
      console.log('⏭️ Skipping duplicate fetchClientPrograms call')
      return { success: true, data: { programs: state.programs, clientDetails: state.clientDetails } }
    }
    
    const requestKey = `composition:fetchClientPrograms:${companyUrl}`
    
    try {
      const result = await apiCallManager.executeRequest(
        requestKey,
        async (signal) => {
          console.log('🔄 Fetching client programs...')
          return await apiService.post(`/${companyUrl}/getclientprograms`, {}, signal)
        },
        {
          onStart: () => set({ isLoadingPrograms: true, error: null }),
          onComplete: async (response) => {
            if (response.success) {
              const { programs, clientDetais: clientDetails } = response.data
              console.log('✅ Programs loaded:', programs)
              console.log('✅ Client details loaded:', clientDetails)
              
              // Auto-select the active program or first program
              const activeProgram = programs.find(p => p.id === clientDetails?.activeprogram_id) || programs[0]
              
              set({ 
                programs,
                clientDetails,
                selectedProgram: activeProgram,
                isLoadingPrograms: false 
              })

              // Fetch compositions for the selected program
              if (activeProgram) {
                await get().fetchClientCompositions(companyUrl, activeProgram.id)
              }
            } else {
              set({ 
                error: response.error,
                isLoadingPrograms: false 
              })
            }
          },
          onError: (error) => {
            if (error.message !== 'Request cancelled') {
              const errorMessage = error.message || 'Failed to fetch client programs'
              set({ 
                error: errorMessage,
                isLoadingPrograms: false 
              })
            }
          }
        }
      )
      return result
    } catch (error) {
      if (error.message !== 'Request cancelled') {
        const errorMessage = error.message || 'Failed to fetch client programs'
        set({ 
          error: errorMessage,
          isLoadingPrograms: false 
        })
        return { success: false, error: errorMessage }
      }
    }
  },

  fetchClientCompositions: async (companyUrl, programId) => {
    const state = get()
    
    // Prevent duplicate concurrent calls for the same program
    if (state.isLoadingCompositions) {
      console.log('⏭️ Skipping duplicate fetchClientCompositions call (already loading)')
      return { success: true }
    }
    
    const requestKey = `fetchClientCompositions:${companyUrl}:${programId}`
    
    try {
      const result = await apiCallManager.executeRequest(
        requestKey,
        async (signal) => {
          console.log('🔄 Fetching client compositions for program:', programId)
          return await apiService.post(`/${companyUrl}/getclientcompositions`, { program_id: programId }, signal)
        },
        {
          onStart: () => set({ isLoadingCompositions: true, error: null }),
          onComplete: (response) => {
            if (response.success) {
              const { programGoal, weights, compositions, vitals } = response.data
              console.log('✅ Compositions loaded for program:', programId, { programGoal, weights, compositions, vitals })
              
              set({ 
                programGoal,
                weights: weights || [],
                compositions: compositions || [],
                vitals: vitals || [],
                isLoadingCompositions: false 
              })
            } else {
              set({ 
                error: response.error,
                isLoadingCompositions: false 
              })
            }
          },
          onError: (error) => {
            if (error.message !== 'Request cancelled') {
              const errorMessage = error.message || 'Failed to fetch client compositions'
              set({ 
                error: errorMessage,
                isLoadingCompositions: false 
              })
            }
          }
        }
      )
      return result
    } catch (error) {
      if (error.message !== 'Request cancelled') {
        const errorMessage = error.message || 'Failed to fetch client compositions'
        set({ 
          error: errorMessage,
          isLoadingCompositions: false 
        })
        return { success: false, error: errorMessage }
      }
    }
  },

  selectProgram: async (companyUrl, program) => {
    set({ selectedProgram: program })
    await get().fetchClientCompositions(companyUrl, program.id)
  },

  clearError: () => {
    set({ error: null })
  },

  // Clear all data (useful for logout or switching users)
  clearAllData: () => {
    set({
      programs: [],
      clientDetails: null,
      selectedProgram: null,
      programGoal: null,
      weights: [],
      compositions: [],
      vitals: [],
      isLoadingPrograms: false,
      isLoadingCompositions: false,
      error: null
    })
  },

  // Helper methods for data processing
  getLatestWeight: () => {
    const { weights } = get()
    if (!Array.isArray(weights) || weights.length === 0) return null
    
    try {
      return weights.reduce((latest, current) => {
        if (!current || !current.measured_at) return latest
        if (!latest || !latest.measured_at) return current
        
        const currentDate = new Date(current.measured_at)
        const latestDate = new Date(latest.measured_at)
        
        // Check if dates are valid
        if (isNaN(currentDate.getTime())) return latest
        if (isNaN(latestDate.getTime())) return current
        
        return currentDate > latestDate ? current : latest
      })
    } catch (error) {
      console.warn('Error getting latest weight:', error)
      return null
    }
  },

  getLatestComposition: () => {
    const { compositions } = get()
    if (!Array.isArray(compositions) || compositions.length === 0) return null
    
    try {
      return compositions.reduce((latest, current) => {
        if (!current || !current.measured_at) return latest
        if (!latest || !latest.measured_at) return current
        
        const currentDate = new Date(current.measured_at)
        const latestDate = new Date(latest.measured_at)
        
        // Check if dates are valid
        if (isNaN(currentDate.getTime())) return latest
        if (isNaN(latestDate.getTime())) return current
        
        return currentDate > latestDate ? current : latest
      })
    } catch (error) {
      console.warn('Error getting latest composition:', error)
      return null
    }
  },

  getLatestVitals: () => {
    const { vitals } = get()
    if (!Array.isArray(vitals) || vitals.length === 0) return null
    
    try {
      return vitals.reduce((latest, current) => {
        if (!current || !current.measured_at) return latest
        if (!latest || !latest.measured_at) return current
        
        const currentDate = new Date(current.measured_at)
        const latestDate = new Date(latest.measured_at)
        
        // Check if dates are valid
        if (isNaN(currentDate.getTime())) return latest
        if (isNaN(latestDate.getTime())) return current
        
        return currentDate > latestDate ? current : latest
      })
    } catch (error) {
      console.warn('Error getting latest vitals:', error)
      return null
    }
  },

  // Calculate BMI from latest weight data
  calculateBMI: () => {
    const latestWeight = get().getLatestWeight()
    // Note: Height would need to be stored somewhere or passed as parameter
    // For now, returning null as height is not available in the API response
    return null
  },

  // Get weight progress data for charts
  getWeightProgress: () => {
    const { weights } = get()
    if (!Array.isArray(weights)) return []
    
    try {
      return weights
        .filter(w => w && w.measured_at) // Filter out invalid entries
        .sort((a, b) => {
          const dateA = new Date(a.measured_at)
          const dateB = new Date(b.measured_at)
          
          // Handle invalid dates
          if (isNaN(dateA.getTime())) return 1
          if (isNaN(dateB.getTime())) return -1
          
          return dateA - dateB
        })
        .map(w => ({
          date: w.measured_at,
          weight: w.weight,
          bodyfat: w.bodyfat,
          leanmuscle: w.leanmuscle,
          waterweight: w.waterweight,
          fatmass: w.fatmass
        }))
    } catch (error) {
      console.warn('Error getting weight progress:', error)
      return []
    }
  },

  // Get composition progress data for charts
  getCompositionProgress: () => {
    const { compositions } = get()
    if (!Array.isArray(compositions)) return []
    
    try {
      return compositions
        .filter(c => c && c.measured_at) // Filter out invalid entries
        .sort((a, b) => {
          const dateA = new Date(a.measured_at)
          const dateB = new Date(b.measured_at)
          
          // Handle invalid dates
          if (isNaN(dateA.getTime())) return 1
          if (isNaN(dateB.getTime())) return -1
          
          return dateA - dateB
        })
        .map(c => ({
          date: c.measured_at,
          neck: c.neck,
          arm: c.arm,
          chest: c.chest,
          waist: c.waist,
          hip: c.hip,
          thigh: c.thigh,
          calf: c.calf,
          forearm: c.forearm
        }))
    } catch (error) {
      console.warn('Error getting composition progress:', error)
      return []
    }
  },

  // Get vitals progress data for charts
  getVitalsProgress: () => {
    const { vitals } = get()
    if (!Array.isArray(vitals)) return []
    
    try {
      return vitals
        .filter(v => v && v.measured_at) // Filter out invalid entries
        .sort((a, b) => {
          const dateA = new Date(a.measured_at)
          const dateB = new Date(b.measured_at)
          
          // Handle invalid dates
          if (isNaN(dateA.getTime())) return 1
          if (isNaN(dateB.getTime())) return -1
          
          return dateA - dateB
        })
        .map(v => ({
          date: v.measured_at,
          resting_heart_rate: v.resting_heart_rate,
          active_heart_rate: v.active_heart_rate,
          resting_blood_pressure: v.resting_blood_pressure,
          active_blood_pressure: v.active_blood_pressure,
          o2_saturation: v.o2_saturation,
          body_temperature: v.body_temperature,
          vo2_max: v.vo2_max
        }))
    } catch (error) {
      console.warn('Error getting vitals progress:', error)
      return []
    }
  }
}))